import { downloadFilename } from "./text.js";
import { LANGUAGES, fingerprint, type Language, type LanguageCode } from "./languages.js";
import { timelineDuration } from "./layout.js";
import { spokenText } from "./speech.js";
import { timelineSchema, type Timeline } from "./timeline.js";
import type { VoiceTimeline } from "./voice-timeline.js";

/**
 * What a video's languages are made from, worked out the same way by the API
 * (to quote and to tell when a language is out of date) and by the worker (to
 * translate and to lay the voice on the clock). One definition, because a
 * quote computed from different lines than the ones translated would charge
 * for something other than what was made.
 */

/** One English line, with where it sits on the finished video's clock and the time it has. */
export interface SourceLine {
  id: string;
  english: string;
  startAt: number;
  seconds: number;
}

export const VOICE_ROLES = new Set(["head-voice", "body-voice", "end-voice"]);

/**
 * A product demo's narration as lines: one per voice track that plays.
 *
 * Each line has until the next voice starts, or until the video ends for the
 * last one, so a translation may use the pause after a line as well as the
 * line's own length. A track with no text is skipped, but it still ends the
 * line before it: that time belongs to a voice the translation cannot replace.
 */
export function demoSourceLines(timeline: Timeline, texts: Record<string, string>): SourceLine[] {
  const total = timelineDuration(timeline);
  const voices = timeline.audio
    .filter((track) => VOICE_ROLES.has(track.role) && track.enabled && track.assetKey)
    .sort((a, b) => a.startAt - b.startAt);

  const lines: SourceLine[] = [];
  voices.forEach((track, index) => {
    const english = (texts[track.id] ?? "").trim();
    if (!english) return;
    const next = voices[index + 1];
    const until = next ? next.startAt : total;
    lines.push({ id: track.id, english, startAt: track.startAt, seconds: Math.max(0, until - track.startAt) });
  });
  return lines;
}

/**
 * The texts `demoSourceLines` reads, keyed by voice track id: a body line's
 * scene script under `voice-<sceneId>`, and the head and end cards' spoken
 * text under `voice-head` and `voice-end`.
 *
 * A card's narration is the text that was actually voiced. Where more than one
 * voice was made for a card, the one the timeline plays (by its file) wins,
 * then the newest; the card's own script is the fallback when no voice row
 * survives.
 *
 * Every text is what is said, without its inline pause markers (`[1.5s]`):
 * nobody reads a marker aloud, so it is not translated, priced or subtitled.
 */
export function demoTexts(input: {
  timeline: Timeline;
  scenes: readonly { id: string; script: string }[];
  bookends: readonly { bookend: string | null; text: string; assetKey: string | null; createdAt: Date }[];
}): Record<string, string> {
  const texts: Record<string, string> = {};
  for (const scene of input.scenes) texts[`voice-${scene.id}`] = spokenText(scene.script);

  for (const [card, trackId] of [
    ["head", "voice-head"],
    ["end", "voice-end"],
  ] as const) {
    const track = input.timeline.audio.find((one) => one.id === trackId);
    const candidates = input.bookends
      .filter((one) => one.bookend === card)
      .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
    const played = candidates.find((one) => track?.assetKey && one.assetKey === track.assetKey) ?? candidates[0];
    const text = spokenText(played?.text ?? input.timeline.sections[card]?.script ?? "");
    if (text) texts[trackId] = text;
  }
  return texts;
}

/** Marks that end a sentence where a space follows: Latin, Arabic, Devanagari and the ellipsis. */
const SPACED_END = /(?<=[.!?؟।…]["'”’)\]]*)\s+/u;
/** Marks that end a sentence with no space after them: Japanese and Chinese full stops. */
const UNSPACED_END = /([。！？])/gu;

/** A script's spoken sentences, without `[1s]` pauses, split as subtitles are. */
export function scriptSentences(script: string): string[] {
  // A `[1s]` pause is the voice's, not a word anybody reads or translates.
  // Line by line, because the paragraph breaks below are sentence ends too.
  return script
    .split("\n")
    .map(spokenText)
    .join("\n")
    .replace(UNSPACED_END, "$1\n")
    .split(/\n+/)
    .flatMap((paragraph) => paragraph.split(SPACED_END))
    .map((sentence) => sentence.trim())
    .filter(Boolean);
}

/**
 * A launch video's script as sentences, sharing the film's length in
 * proportion to their length in characters (code points, so a script in any
 * writing system is measured fairly). Ids are `s1`…`sN`.
 */
export function launchSourceLines(script: string, filmSeconds: number, timeline?: VoiceTimeline | null): SourceLine[] {
  const sentences = scriptSentences(script);
  // Measured, when the voice was recorded with its timings and still says
  // these sentences; shared out by length otherwise, as before.
  const measured =
    timeline &&
    timeline.sentences.length === sentences.length &&
    timeline.sentences.every((one, index) => one.text === sentences[index])
      ? timeline.sentences
      : null;
  if (measured) {
    return measured.map((sentence, index) => ({
      id: `s${index + 1}`,
      english: sentence.text,
      startAt: sentence.start,
      seconds: Math.max(0, sentence.end - sentence.start),
    }));
  }
  const length = (text: string) => [...text].length;
  const total = sentences.reduce((sum, sentence) => sum + length(sentence), 0);
  const film = Number.isFinite(filmSeconds) && filmSeconds > 0 ? filmSeconds : 0;

  let startAt = 0;
  return sentences.map((english, index) => {
    const seconds = total > 0 ? (film * length(english)) / total : 0;
    const line = { id: `s${index + 1}`, english, startAt, seconds };
    startAt += seconds;
    return line;
  });
}

/**
 * The English characters a quote is priced on: every source line's text.
 * Counted in UTF-16 units, as `String.length` does, because that is what the
 * voice provider bills.
 */
export function englishCharacters(lines: readonly SourceLine[]): number {
  return lines.reduce((sum, line) => sum + line.english.length, 0);
}

/**
 * What a product demo's languages were made from.
 *
 * The timeline is read through its schema first, so a stored document and the
 * same document as the editor holds it (defaults filled, unknown keys dropped)
 * give the same fingerprint. The browser fingerprints the timeline it renders
 * the master from, and the API fingerprints the stored one; they must agree.
 */
export function demoFingerprint(timeline: Timeline): string {
  const parsed = timelineSchema.safeParse(timeline);
  return fingerprint(parsed.success ? parsed.data : timeline);
}

/** What a launch version's languages were made from: its words and its picture. */
export function launchFingerprint(version: { script: string | null; videoKey: string | null }): string {
  return fingerprint({ script: version.script ?? "", videoKey: version.videoKey ?? "" });
}

/**
 * Where a master's fingerprint is kept: in its storage key's folder,
 * `master-<fingerprint>/…`. A key never changes once written, so the
 * fingerprint cannot drift from the file it describes, and nothing in the
 * schema has to grow for it.
 */
export function masterKeyPrefix(fingerprintValue: string): string {
  return `master-${fingerprintValue}`;
}

/** The fingerprint a master was rendered from, or null for a key not written by `masterKeyPrefix`. */
export function masterFingerprintOf(key: string): string | null {
  const match = /^master-([0-9a-f]{16})\//.exec(key);
  return match ? match[1] : null;
}

/**
 * A language's download name: the project's name and the language's English
 * name, never its native one, because `downloadFilename` keeps only Latin
 * letters and "Acme-العربية" would lose the very word that tells the files apart.
 */
export function languageFilename(projectName: string, language: Language, extension: string): string {
  return downloadFilename(`${projectName}-${language.name}`, extension);
}

/** The master's own file name. */
export function masterFilename(projectName: string): string {
  return downloadFilename(`${projectName}-master`, "mp4");
}

/**
 * The language a product demo was spoken in, from what transcription
 * reported: a code ("de", "pt-BR") or a name ("german"). Anything not in the
 * table is treated as English, the default the narration is written in.
 */
export function sourceLanguageOf(reported: string | null | undefined): LanguageCode {
  const value = (reported ?? "").trim().toLowerCase();
  if (!value) return "en";
  const code = value.split(/[-_]/)[0];
  const found = LANGUAGES.find((one) => one.code === code || one.name.toLowerCase() === value);
  return found?.code ?? "en";
}

/**
 * The storage folder every file of one language is written under: its voiced
 * lines, its video, audio, subtitles and script. One folder per language, so
 * what belongs to it can be found and removed without a list of keys, and a
 * file left behind by a job that did not finish is still found by the next.
 * Short enough for `buildAssetKey`, which keeps 40 characters of a prefix.
 */
export function languageKeyPrefix(languageId: string): string {
  return `lang-${languageId.replace(/-/g, "")}`;
}

/** What a language says when it could not be made and nothing more specific can be told. */
export const GENERIC_LANGUAGE_ERROR = "Something went wrong while making this language.";

/** What a ready language says when a free revoice of its changed lines did not finish. */
export const REVOICE_ERROR = "The changed lines could not be voiced — try again";

/**
 * The fingerprint a language is left with when its files were made from
 * English that is no longer current, its own fingerprint was already
 * replaced, and there is no record of the version it had (`MadeVersion`) —
 * a row from before that record was kept. It matches no real fingerprint, so
 * the language shows out of date and offers "Update".
 */
export const SUPERSEDED_SOURCE = "superseded";

/** What a language says when an update did not finish and the version it had is still there. */
export const UPDATE_ERROR = "The update did not finish — the previous version is still here. Try again";

/**
 * Whether a language has been made: it has the files it was ready with. A
 * launch film's language has its MP4; a product demo's has its voice track,
 * since its MP4 is the browser export's and may not exist yet (a part-1 demo
 * language has both).
 */
export function languageIsMade(row: { videoKey: string | null; audioKey?: string | null }): boolean {
  return Boolean(row.videoKey || row.audioKey);
}

/**
 * The version a made language had when it was asked to be made again: its
 * voiced lines (each naming its clip), its words on screen, its depth, the
 * fingerprint it was made from and the price it was charged. A remake writes
 * new, unvoiced lines and — once translated — new texts and depth over these,
 * while its files stay those of the version it had until the new voice lands.
 * Kept on the row (`made`) from the write that starts the remake until that
 * landing, so a remake that is discarded or does not finish puts back exactly
 * the version the files belong to: its voice, subtitles and texts, and its
 * own fingerprint — current if it was current, out of date if it was.
 */
export interface MadeVersion {
  lines: unknown[];
  texts: unknown[];
  depth: "voice" | "video";
  sourceFingerprint: string;
  /** The price it was charged, as stored (four places). */
  priceUsd: string;
}

/**
 * The record to keep when a language is about to be made again, or undefined
 * when there is nothing to keep — it was never made — or a record is already
 * kept: a language in review or failed is not a made version (its lines are
 * an unvoiced draft), and one being made again since it was ready already
 * holds the version it had, which must not be replaced by the draft.
 */
export function madeVersionOf(row: {
  status: string;
  videoKey: string | null;
  audioKey: string | null;
  lines: unknown;
  texts: unknown;
  depth: string;
  sourceFingerprint: string;
  priceUsd: unknown;
}): MadeVersion | undefined {
  if (row.status !== "ready" || !languageIsMade(row)) return undefined;
  return {
    lines: Array.isArray(row.lines) ? row.lines : [],
    texts: Array.isArray(row.texts) ? row.texts : [],
    depth: row.depth === "video" ? "video" : "voice",
    sourceFingerprint: row.sourceFingerprint,
    priceUsd: String(row.priceUsd),
  };
}

/** A kept version as stored, if it reads as one. */
export function readMadeVersion(value: unknown): MadeVersion | null {
  const raw = value as Partial<MadeVersion> | null | undefined;
  if (!raw || typeof raw !== "object") return null;
  if (!Array.isArray(raw.lines) || !Array.isArray(raw.texts)) return null;
  if (typeof raw.sourceFingerprint !== "string" || !raw.sourceFingerprint) return null;
  const price = Number(raw.priceUsd);
  if (typeof raw.priceUsd !== "string" || !Number.isFinite(price) || price < 0) return null;
  return {
    lines: raw.lines,
    texts: raw.texts,
    depth: raw.depth === "video" ? "video" : "voice",
    sourceFingerprint: raw.sourceFingerprint,
    priceUsd: raw.priceUsd,
  };
}

/**
 * The fields that put a made language back as it was, from the version it
 * kept; or, for a row with no readable record, only a fingerprint that
 * matches nothing (`SUPERSEDED_SOURCE`), so it shows out of date.
 */
export function restoredFields(made: unknown): Partial<MadeVersion> & { sourceFingerprint: string } {
  return readMadeVersion(made) ?? { sourceFingerprint: SUPERSEDED_SOURCE };
}

/**
 * Where a language being made again is left when its job does not finish,
 * if it still has the files it was ready with: ready with them and with the
 * version they belong to (`restoredFields`), saying the update did not
 * finish. Null for a language that was never made — that one has failed.
 *
 * It stays downloadable and in the zip, and nothing is charged: charging
 * happens when a language is marked ready by the job that made it.
 */
export function failedUpdate(row: {
  status: string;
  videoKey: string | null;
  audioKey?: string | null;
  made?: unknown;
}): ({ error: string } & ReturnType<typeof restoredFields>) | null {
  if (row.status !== "translating" && row.status !== "voicing") return null;
  if (!languageIsMade(row)) return null;
  return { error: UPDATE_ERROR, ...restoredFields(row.made) };
}
